import type { SubmitCareerExerciseMutation } from 'core/graphql';

export interface IExerciseApplyReport {
  writtenTargets: string[];
  skippedTargets: string[];
  profileCompleteness: number;
}

export interface IExerciseSubmitResult {
  success: boolean;
  /** Медали и коды, выданные ИМЕННО этой сдачей. Пусто — норма. */
  grantedTitles: string[];
  promoCodes: string[];
  report: IExerciseApplyReport | null;
  lessonsCompleted: number;
  lessonsTotal: number;
  /**
   * Код первой серверной ошибки (`tmsis/career/constants.py`: `invalid_value`,
   * `required_missing`, `lesson_locked`…). Пусто — ошибки нет или ответа не
   * было вовсе (сеть, отказ в доступе).
   */
  errorCode: string;
  /** Сырое `field` ошибки: `field:<id>`, `lessonId`, `answers`, `student`. */
  errorField: string;
  /** Id поля анкеты, если ошибка к нему привязана (`field:<id>`), иначе `null`. */
  errorFieldId: string | null;
}

/**
 * Префикс, которым `career/services/exercise_values.py` привязывает ошибку к
 * полю анкеты: `field:<pk>`. Id поля в LMS — тот же pk (`resolve_id`).
 */
const FIELD_ERROR_PREFIX = 'field:';

export const exerciseErrorFieldId = (field?: string | null): string | null => {
  if (!field || !field.startsWith(FIELD_ERROR_PREFIX)) return null;
  return field.slice(FIELD_ERROR_PREFIX.length) || null;
};

/**
 * ТЗ 0039 — ответ сдачи анкеты → модель для формы.
 *
 * 🔴 Серверный `message` сюда НЕ попадает намеренно: сервер пишет его
 * по-русски (он для логов и Sentry), а интерфейс LMS польский. Раньше хук
 * отдавал `errorMessage`, форма показывала его напрямую — и студент читал
 * «Неверный e-mail.». Теперь наружу выходят только `code` и `field`, текст
 * выбирает форма по коду (`getExerciseErrorText`). Нет поля — нет соблазна.
 */
export const transformExerciseSubmitResult = (
  data: SubmitCareerExerciseMutation | null | undefined,
): IExerciseSubmitResult => {
  const payload = data?.submitCareerLessonExercise;
  const error = (payload?.customErrors ?? []).find(Boolean);

  return {
    success: Boolean(payload?.success),
    grantedTitles: [...(payload?.reward?.grantedTitles ?? [])],
    promoCodes: [...(payload?.reward?.promoCodes ?? [])],
    report: payload?.applyReport
      ? {
          writtenTargets: [...payload.applyReport.writtenTargets],
          skippedTargets: [...payload.applyReport.skippedTargets],
          profileCompleteness: payload.applyReport.profileCompleteness,
        }
      : null,
    lessonsCompleted: payload?.lessonsCompleted ?? 0,
    lessonsTotal: payload?.lessonsTotal ?? 0,
    errorCode: error?.code ?? '',
    errorField: error?.field ?? '',
    errorFieldId: exerciseErrorFieldId(error?.field),
  };
};
